import React from 'react';
import ReactDOM from 'react-dom';
import Popper from 'popper.js';

type Props = {
    content: string;
    position?: 'right' | 'left' | 'top' | 'bottom';
}

const getScrollableParents = (element: Element | null): Element[] => {
    const parents: Element[] = [];
    let parent = element?.parentElement;
    while (parent) {
        const style = window.getComputedStyle(parent);
        const overflow = style.overflow + style.overflowX + style.overflowY;
        if (/(auto|scroll)/.test(overflow)) {
            parents.push(parent);
        }
        parent = parent.parentElement;
    }
    return parents;
};

export function ModalTooltip({ content, position = 'right' }: Props) {
    const [tooltipShow, setTooltipShow] = React.useState(false);
    const btnRef = React.useRef(null);
    const tooltipRef = React.useRef(null);
    const popperInstance = React.useRef<Popper | null>(null);
    const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const cancelClose = () => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    };
    
    const openTooltip = () => {
        cancelClose();
        if (popperInstance.current) {
            popperInstance.current.destroy();
            popperInstance.current = null;
        }
        if (btnRef.current && tooltipRef.current) {
            popperInstance.current = new Popper(btnRef.current, tooltipRef.current, {
                placement: position,
                modifiers: {
                    preventOverflow: {
                        boundariesElement: 'viewport'
                    }
                }
            });
        }
        setTooltipShow(true);
    };
    
    const closeTooltip = () => {
        closeTimer.current = setTimeout(() => {
            if (popperInstance.current) {
                popperInstance.current.destroy();
                popperInstance.current = null;
            }
            setTooltipShow(false);
        }, 100);
    };

    React.useEffect(() => {
        return () => {
            cancelClose();
            if (popperInstance.current) {
                popperInstance.current.destroy();
                popperInstance.current = null;
            }
        };
    }, []);

    React.useEffect(() => {
        if (!tooltipShow) {
            return;
        }

        const handleScroll = () => {
            if (popperInstance.current) {
                popperInstance.current.scheduleUpdate();
            }
        };

        const scrollableParents = getScrollableParents(btnRef.current);
        scrollableParents.forEach(parent => parent.addEventListener('scroll', handleScroll));
        window.addEventListener('scroll', handleScroll);

        return () => {
            scrollableParents.forEach(parent => parent.removeEventListener('scroll', handleScroll));
            window.removeEventListener('scroll', handleScroll);
        };
    }, [tooltipShow]);

    const getArrowStyle = () => {
        const baseStyle = { fill: '#1e293b' };
        switch (position) {
            case 'right':
                return { ...baseStyle, left: '-0.75rem', top: '50%', transform: 'translateY(-50%) rotate(90deg)' };
            case 'left':
                return { ...baseStyle, right: '-0.75rem', top: '50%', transform: 'translateY(-50%) rotate(-90deg)' };
            case 'top':
                return { ...baseStyle, bottom: '-0.75rem', left: '50%', transform: 'translateX(-50%) rotate(180deg)' };
            case 'bottom':
                return { ...baseStyle, top: '-0.75rem', left: '50%', transform: 'translateX(-50%)' };
            default:
                return { ...baseStyle, left: '-0.75rem', top: '50%', transform: 'translateY(-50%) rotate(90deg)' };
        }
    };

    const getTooltipMargin = () => {
        switch (position) {
            case 'right':
                return 'ml-2';
            case 'left':
                return 'mr-2';
            case 'top':
                return 'mb-2';
            case 'bottom':
                return 'mt-2';
            default:
                return 'ml-2';
        }
    };

    const tooltipEl = (
        <div
            className={
                (tooltipShow ? '' : 'hidden ') +
                `border-0 ${getTooltipMargin()} block z-50 font-normal leading-normal text-left no-underline break-words rounded-lg shadow-lg`
            }
            style={{ backgroundColor: '#1e293b', position: 'absolute' }}
            ref={tooltipRef}
            onMouseEnter={cancelClose}
            onMouseLeave={closeTooltip}
        >
            <div className='text-white text-xs px-3 py-2 max-w-xs break-words' style={{ opacity: 1, maxWidth: '280px' }}>
                {content}
                <svg 
                    className='absolute h-3 w-3'
                    style={getArrowStyle()} 
                    x='0px' 
                    y='0px' 
                    viewBox='0 0 255 255' 
                    xmlSpace='preserve'
                >
                    <polygon points='0,0 127.5,127.5 255,0' />
                </svg>
            </div>
        </div>
    );

    return (
        <>
            <span
                className='cursor-pointer inline-flex items-center justify-center p-2'
                onMouseEnter={openTooltip}
                onMouseLeave={closeTooltip}
                ref={btnRef}
            >
                <span className='text-blue-600 hover:text-blue-800 font-semibold inline-flex items-center justify-center w-4 h-4 text-xs rounded-full border border-blue-600 hover:border-blue-800 transition-colors'>
                    ?
                </span>
            </span>
            {ReactDOM.createPortal(tooltipEl, document.body)}
        </>
    );
}
